import { useCallback, useEffect } from 'react';
import { useAdminAuthStore, hasRole, type SystemRole } from '../store';
import {
  signInWithMicrosoft,
  signOutMicrosoft,
  getInternalMsalClient,
} from '@/shared/lib/msal-internal-auth';
import type { AuthUser } from '@/shared/lib/auth-store-factory';

interface AdminAuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  loading: boolean;
  login: () => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  hasSystemRole: (role?: SystemRole) => boolean;
  isPlatformAdmin: boolean;
}

const useAdminAuth = (): AdminAuthState => {
  const store = useAdminAuthStore();

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // Use the stored ID token for authentication (contains roles, name, etc).
    // Access tokens acquired via OIDC scopes are for Microsoft Graph and
    // don't contain the app roles that the backend needs.
    return store.token;
  }, [store.token]);

  const login = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    const result = await signInWithMicrosoft();
    if (!result.success) {
      return result;
    }

    try {
      const client = await getInternalMsalClient();
      const accounts = client.getAllAccounts();
      const account = accounts[0];

      if (!account) {
        return { success: false, error: "Conta não encontrada após autenticação" };
      }

      const claims = account.idTokenClaims as Record<string, unknown>;
      const roles = (claims.roles as string[]) || [];
      const systemRole = roles[0] || null;

      if (!systemRole || !["root", "commercial", "analyst"].includes(systemRole)) {
        await signOutMicrosoft();
        return { success: false, error: "Papel de administrador inválido" };
      }

      const user: AuthUser = {
        name: (claims.name as string) || "",
        email: (claims.preferred_username as string) || (claims.email as string) || "",
        id: (claims.oid as string) || (claims.sub as string) || "",
        system_role: systemRole,
      };

      // Store the ID token — it contains the roles claim that the backend requires.
      // Access tokens acquired via OIDC scopes are for Microsoft Graph.
      store.setUser(user);
      store.setToken(account.idToken || null);
      store.setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      await signOutMicrosoft();
      return {
        success: false,
        error: error instanceof Error ? error.message : "Falha ao processar autenticação",
      };
    }
  }, [store]);

  const logout = useCallback(async () => {
    await signOutMicrosoft();
    store.reset();
    localStorage.removeItem("examfy-admin-auth-storage");
  }, [store]);

  const checkRole = useCallback((role?: SystemRole): boolean => {
    const user = useAdminAuthStore.getState().user;
    return hasRole(user, role);
  }, []);

  const isPlatformAdmin = hasRole(useAdminAuthStore.getState().user);

  const isAuthenticated = store.isAuthenticated;
  const user = store.user;
  const loading = !store.hasHydrated;

  useEffect(() => {
    if (store.hasHydrated && user && !hasRole(user)) {
      logout();
    }
  }, [store.hasHydrated, user, logout]);

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    getAccessToken,
    hasSystemRole: checkRole,
    isPlatformAdmin,
  };
};

export { useAdminAuth };
export type { SystemRole };
export type { AuthUser as User };
